import App from 'next/app';
import Router from 'next/router';
import React from 'react';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/react-hooks';
import { getStoredState, persistStore } from 'redux-persist';
import withRedux from 'next-redux-wrapper';
import { Global } from '@emotion/core';
import get from 'lodash/get';

import makeStore from '../store';

import withApollo from '../hocs/withApollo';

import Layout from '../components/Common/Layout';

import { windowAvailable, storedStateRetreived } from '../redux/app';
import { browserResized, DEFAULT_DESKTOP_SIZE } from '../redux/env';
import { updateRoute } from '../redux/route';
import { updateScrollPosition } from '../redux/scrollPosition';
import persist from '../redux/persist';
import { initialize } from '../redux/app';

import { buildURLFromQueryInfo } from '../utils/urls/urlGenerator';
import bugsnag from '../utils/bugsnag';

import AppStyles from '../css/AppStyles';
import NormalizeStyles from '../css/NormalizeStyles';

let ErrorBoundary = bugsnag.getPlugin('react');

class PaperlessPostApp extends App {
  static async getInitialProps(props) {
    const { Component, ctx, router } = props;
    const { store, req } = ctx;

    // This triggers the following requests -
    //    -- singleCardRoute.singleCardOriginalRoute - listened for in category's getInitialProps
    // Note requests that result like this and their completion should be listened for
    // in the related page's getInitialProps
    // Pass along the router because we can't use import Router from 'next/router' on server side.
    if (req && req.url) {
      const startingURL = buildURLFromQueryInfo(router.pathname, router.query);
      store.dispatch(
        updateRoute(startingURL, { ...router, asPath: startingURL }),
      );
    }

    let childInitialPropsPromise = Component.getInitialProps
      ? Component.getInitialProps(ctx, router)
      : Promise.resolve();

    const allInitialPropsPromises = [childInitialPropsPromise];

    // IMPORTANT: dispatch this here so that the action stream is primed and all promises have had a chance to resolve...
    store.dispatch(initialize());

    // Once all the data loading tasks have resolved, we're able to start rendering...
    await Promise.all(allInitialPropsPromises);

    return { ...store };
  }

  componentDidMount() {
    const { store } = this.props;

    const size = {
      width: get(
        window,
        'document.documentElement.clientWidth',
        DEFAULT_DESKTOP_SIZE.width,
      ),
    };

    store.dispatch(windowAvailable());
    store.dispatch(browserResized(size));

    // Pass along the router because we can't use import Router from 'next/router' on server side.
    Router.events.on('routeChangeComplete', url => {
      store.dispatch(updateRoute(url, Router.router));
      store.dispatch(updateScrollPosition(url, Router.router));
    });

    const persistConfig = persist();
    getStoredState(persistConfig, (err, dehydratedState) => {
      store.dispatch(storedStateRetreived(dehydratedState));
      persistStore(store, persist());
    });
  }

  render() {
    const { Component, pageProps, store, apollo } = this.props;

    return (
      <ErrorBoundary>
        <Global styles={AppStyles} />
        <Global styles={NormalizeStyles} />
        <Provider store={store}>
          <ApolloProvider client={apollo}>
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </ApolloProvider>
        </Provider>
      </ErrorBoundary>
    );
  }
}

export default withApollo(withRedux(makeStore)(PaperlessPostApp));
